import { makeStyles, mergeClasses, shorthands } from "@fluentui/react-components";
import { REFINED } from "../risikoanalyseStep/tokens";

/**
 * Segmented-Control (Design-Stil A) für eine Auswahl aus wenigen Werten —
 * Kontaktstatus in den Anlage-Masken, sonst auch Ja/Nein, privat/geschäftlich
 * oder Firma/Privat. Aktives Segment Aubergine gefüllt mit weißem Text +
 * Punkt, inaktive ruhig grau mit gedämpftem Punkt.
 *
 * Reine Präsentation: die Komponente kennt keine Fachlogik, und `value` wird
 * nur auf Gleichheit geprüft — steht es nicht in `options`, ist schlicht kein
 * Segment markiert (ohne Fehler). Wer projizierte Werte anzeigt, sorgt selbst
 * dafür, dass beides zusammenpasst (siehe data/kontaktstatusSegment.ts).
 */

const useStyles = makeStyles({
  wrap: {
    display: "inline-flex",
    alignSelf: "flex-start",
    flexWrap: "wrap",
    maxWidth: "100%",
    columnGap: "4px",
    rowGap: "4px",
    ...shorthands.padding("4px"),
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("12px"),
    backgroundColor: REFINED.aub50,
  },
  seg: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "8px",
    ...shorthands.borderWidth("0"),
    ...shorthands.borderRadius("9px"),
    ...shorthands.padding("9px", "18px"),
    backgroundColor: "transparent",
    fontFamily: REFINED.fontUI,
    fontSize: "0.92rem",
    fontWeight: 600,
    color: REFINED.ink2,
    cursor: "pointer",
    transitionProperty: "background-color, color, box-shadow",
    transitionDuration: "140ms",
    transitionTimingFunction: "ease",
  },
  segActive: {
    backgroundColor: REFINED.aub,
    color: REFINED.white,
    boxShadow: "0 1px 3px rgba(58,12,49,0.25)",
  },
  dot: {
    width: "7px",
    height: "7px",
    ...shorthands.borderRadius("999px"),
    backgroundColor: REFINED.ink3,
    flexShrink: 0,
  },
  dotActive: { backgroundColor: REFINED.white },
});

export function StatusSegmented({
  value,
  options,
  onChange,
  ariaLabel,
}: {
  value: string;
  options: string[];
  onChange: (v: string) => void;
  ariaLabel?: string;
}) {
  const s = useStyles();
  return (
    <div className={s.wrap} role="radiogroup" aria-label={ariaLabel}>
      {options.map((opt) => {
        const active = value === opt;
        return (
          <button
            key={opt}
            type="button"
            role="radio"
            aria-checked={active}
            className={mergeClasses(s.seg, active && s.segActive)}
            onClick={() => onChange(opt)}
          >
            <span className={mergeClasses(s.dot, active && s.dotActive)} />
            {opt}
          </button>
        );
      })}
    </div>
  );
}

export default StatusSegmented;
